import { api } from "@/loanLinkApi";
import type { Sentence, Transcriber } from "@/models/Conversation";
import {
	RealtimeClient,
	type RecognitionResult,
} from "@speechmatics/real-time-client";
import { ref } from "vue";

export function recognitionResultsToSentences(
	results: RecognitionResult[],
	partialResults?: RecognitionResult[],
): Sentence[] {
	const sentences: Sentence[] = [];

	for (const result of results) {
		const { alternatives, is_eos, attaches_to } = result;
		if (!alternatives) {
			continue;
		}

		const { content, speaker } = alternatives[0] ?? {};
		if (!content) {
			continue;
		}

		const lastSentence = sentences.at(-1);

		const shouldCreateNewSentence =
			!lastSentence || lastSentence.speaker !== speaker;

		if (shouldCreateNewSentence) {
			sentences.push({
				sentence: content,
				speaker,
				finished: is_eos ?? false,
				startTime: result.start_time,
			});

			continue;
		}

		let newSentence = lastSentence.sentence;
		if (attaches_to !== "previous") {
			newSentence += " ";
		}
		newSentence += content;

		lastSentence.sentence = newSentence;

		if (is_eos) {
			lastSentence.finished = true;
		}
	}

	// Process partial results if available and last sentence is not finished
	if (partialResults && partialResults.length > 0) {
		let partialContent = "";
		for (const result of partialResults) {
			const { alternatives, attaches_to } = result;
			if (!alternatives) {
				continue;
			}

			const { content } = alternatives[0] ?? {};

			if (attaches_to !== "previous") {
				partialContent += " ";
			}

			partialContent += content;
		}

		const lastSentence = sentences.at(-1);
		if (lastSentence) {
			lastSentence.partialSentence = partialContent;
		} else {
			sentences.push({
				partialSentence: partialContent,
				speaker: "S1",
				finished: false,
				startTime: 0,
			});
		}
	}

	return sentences;
}

export function useSpeechmaticsTranscriber(): Transcriber {
	const transcription = ref<RecognitionResult[]>([]);
	const partialTranscriptionResults = ref<RecognitionResult[]>([]);

	const sentences = ref<Sentence[]>([]);

	let mediaRecorder: MediaRecorder | undefined;
	let highestFinalEndTime = 0;

	async function start(stream: MediaStream) {
		try {
			const client = new RealtimeClient();
			client.addEventListener("receiveMessage", ({ data }) => {
				if (data.message === "AddPartialTranscript") {
					partialTranscriptionResults.value = data.results.filter(
						(result) => result.start_time >= highestFinalEndTime,
					);
				} else if (data.message === "AddTranscript") {
					if (data.results.length === 0) {
						return;
					}

					transcription.value.push(...data.results);

					highestFinalEndTime =
						data.results[data.results.length - 1]?.end_time ?? 0;

					partialTranscriptionResults.value =
						partialTranscriptionResults.value.filter(
							(result) => result.start_time >= highestFinalEndTime,
						);
				}

				sentences.value = recognitionResultsToSentences(
					transcription.value,
					partialTranscriptionResults.value,
				);
			});

			const jwt = await api.conversation
				.getTranscriptionToken()
				.then((res) => res.data.secret.key);
			await client.start(jwt, {
				transcription_config: {
					language: "nl",
					enable_partials: true,
					diarization: "speaker",
					// max_delay: 2,
					max_delay_mode: "flexible",
				},
			});

			mediaRecorder = new MediaRecorder(stream);
			mediaRecorder.ondataavailable = (e) => {
				if (e.data.size > 0) {
					client.sendAudio(e.data);
				}
			};
			mediaRecorder.onstop = async () => {
				await client.stopRecognition();
			};
			mediaRecorder.onpause = async () => {
				await client.stopRecognition();
			};
			mediaRecorder.onresume = async () => {
				await client.start(jwt, {
					transcription_config: {
						language: "nl",
						enable_partials: true,
					},
				});
			};
			mediaRecorder.onerror = async () => {
				await client.stopRecognition();
			};

			mediaRecorder.start(100);
		} catch (err) {
			mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
			mediaRecorder?.stop();

			throw err;
		}
	}

	function stop() {
		mediaRecorder?.stop();
		mediaRecorder = undefined;
	}

	return {
		start,
		stop,
		transcription: sentences,
		rawTranscription: transcription,
	};
}
